import React from 'react';
import I18n from 'app/i18n';
import csrfToken from 'app/helpers/csrf-token';
import {configStore, userStore} from 'app/store/global';
import {locationQuery} from 'app/utils/navigate/location';
import {modalConfirm} from 'app/utils/modals/popups';
import {toastSuccess, toastError} from 'app/components/toasts/liteToast';
import Post from 'app/utils/Post';
import Delete from 'app/utils/Delete';
import Get from 'app/utils/Get';
import logEvent from 'app/amplitude/log_event';
import {pushAnalyticsEvent} from 'app/utils/analytics';

/*
 * Shared flow for OAuth/OpenID providers. The provider id is used as the i18n scope
 * (`<provider>.errors.*`), the query param prefix (`<provider>_auth_error`) and the link endpoints (`/<provider>/*`).
 */
export type ExternalAuthProvider = 'google' | 'steam' | 'battlenet';

export interface ExternalAuthFlowOptions {
    redirectUrl?: string;
    promocode?: string;
    siteLang?: string;
    target?: string;
}

export const startExternalAuthFlow = (authPath: string, {
    redirectUrl,
    promocode,
    siteLang,
    target = '_top'
}: ExternalAuthFlowOptions = {}): void => {
    const form = document.createElement('form');

    form.method = 'post';
    form.action = authPath;
    form.target = target;

    const fields: Record<string, string> = {
        authenticity_token: csrfToken() || '',
        site_lang: siteLang || configStore.CONFIG?.lang || 'en'
    };

    if (redirectUrl) {
        fields.redirect_url = redirectUrl;
    }

    if (promocode) {
        fields.promocode = promocode;
    }

    Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');

        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();
};

const consumeQueryParams = (...names: string[]): void => {
    const url = new URL(window.location.href);

    names.forEach(name => url.searchParams.delete(name));
    window.history.replaceState({}, '', url.toString());
};

const knownKeyOr = (key: string, knownKeys: string[], fallbackKey: string): string => knownKeys.includes(key)
    ? key
    : fallbackKey;

export const showExternalAuthError = (provider: ExternalAuthProvider, knownKeys: string[]): void => {
    const param = `${provider}_auth_error`;
    const errorKey = locationQuery()[param];

    if (!errorKey) {
        return;
    }

    consumeQueryParams(param);
    toastError(I18n.t(`${provider}.errors.${knownKeyOr(errorKey, knownKeys, 'failed_to_authenticate')}`));
};

export const showExternalRegistrationSuccess = (provider: ExternalAuthProvider): void => {
    const param = `${provider}_registration`;

    if (!locationQuery()[param]) {
        return;
    }

    consumeQueryParams(param);
    logEvent('REGISTRATION_FORM_SENT', {method: provider});
    pushAnalyticsEvent('registration_complete', {
        method: provider,
        placement: 'registration_popup'
    });
};

export interface PendingLinkResponse {
    success?: boolean
    error?: string
}

interface LinkConfirmationOptions<T extends PendingLinkResponse> {
    provider: ExternalAuthProvider
    linked: boolean
    errorKeys: string[]
    renderDetails: (data: T) => React.ReactNode
}

const confirmLink = (provider: ExternalAuthProvider): void => {
    new Post({url: `/${provider}/link_account`})
        .execute()
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                toastSuccess(data.message || I18n.t(`${provider}.success.account_linked`));
                userStore.updateProfile();
            } else {
                toastError(data.error || I18n.t(`${provider}.errors.failed_to_link_account`));
            }
        })
        .catch(() => {
            toastError(I18n.t(`${provider}.errors.failed_to_link_account`));
        });
};

const cancelLink = (provider: ExternalAuthProvider): void => {
    new Delete({url: `/${provider}/cancel_link`})
        .execute()
        .catch(() => null);
};

export const showExternalLinkConfirmation = <T extends PendingLinkResponse>({
    provider, linked, errorKeys, renderDetails
}: LinkConfirmationOptions<T>): void => {
    const pendingParam = `${provider}_link_pending`;
    const errorParam = `${provider}_link_error`;
    const params = locationQuery();
    const errorKey = params[errorParam];

    if (!params[pendingParam] && !errorKey) {
        return;
    }

    consumeQueryParams(pendingParam, errorParam);

    if (errorKey) {
        toastError(I18n.t(`${provider}.errors.${knownKeyOr(errorKey, errorKeys, 'failed_to_link_account')}`));
        return;
    }

    if (linked) {
        toastError(I18n.t(`${provider}.errors.user_already_linked`));
        return;
    }

    new Get({url: `/${provider}/pending_link`})
        .execute()
        .then(response => response.json())
        .then((data: T) => {
            if (!data.success) {
                toastError(data.error || I18n.t(`${provider}.errors.invalid_or_expired_link`));
                return;
            }

            modalConfirm(
                {element: <div className="steam-link-confirm">{renderDetails(data)}</div>, react: true},
                () => confirmLink(provider),
                () => cancelLink(provider)
            );
        })
        .catch(() => {
            toastError(I18n.t(`${provider}.errors.${provider}_unavailable`));
        });
};
